import React, { useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { useSelector } from "react-redux";
import {
  selectLivePrices,
  selectMarketData,
} from "../../Redux/Reducers/selectors";
import { formatValue } from "../Methods";
import "./tickerTape.css";

const DEFAULT_TICKERS = [
  "SPY",
  "QQQ",
  "DIA",
  "IWM",
  "AAPL",
  "MSFT",
  "NVDA",
  "AMZN",
  "GOOGL",
  "TSLA",
  "META",
  "AMD",
  "AVGO",
  "PLTR",
  "CRWD",
  "SNOW",
  "PANW",
  "ORCL",
  "NFLX",
  "COIN",
  "MSTR",
  "HOOD",
  "RKLB",
  "IONQ",
  "WMT",
  "COST",
  "GE",
  "CVX",
  "XOM",
  "BRK.B",
];

const BADGE_COLORS = ["#06b6d4", "#3b82f6", "#8b5cf6", "#f59e0b", "#6366f1"];

function hasValidPrice(current) {
  return current != null && current !== "" && Number(current) !== 1;
}

function normalizeToTapeEntry(entry) {
  if (!entry) return null;

  const symbol = entry.ticker || entry.symbol;
  if (!symbol) return null;

  const current = entry.current ?? entry.price;
  const last = entry.last ?? entry.yestPrice;

  return {
    symbol: String(symbol).toUpperCase(),
    current,
    last,
  };
}

function normalizeSymbolKey(symbol) {
  return String(symbol || "")
    .toUpperCase()
    .replace(/\//g, ".");
}

function buildMergedEntryMap(marketData, livePrices) {
  const map = new Map();

  if (Array.isArray(marketData)) {
    marketData.forEach((entry) => {
      const normalized = normalizeToTapeEntry(entry);
      if (normalized) {
        map.set(normalizeSymbolKey(normalized.symbol), normalized);
      }
    });
  }

  if (Array.isArray(livePrices)) {
    livePrices.forEach((entry) => {
      const normalized = normalizeToTapeEntry(entry);
      if (!normalized) return;

      const key = normalizeSymbolKey(normalized.symbol);
      const existing = map.get(key);
      const incomingHasPrice = hasValidPrice(normalized.current);
      const existingHasPrice = hasValidPrice(existing?.current);

      if (!existing || incomingHasPrice || !existingHasPrice) {
        map.set(key, {
          symbol: normalized.symbol,
          current: incomingHasPrice
            ? normalized.current
            : existing?.current ?? normalized.current,
          last: incomingHasPrice
            ? normalized.last
            : existing?.last ?? normalized.last,
        });
      }
    });
  }

  return map;
}

function formatPctFromMarket(current, last) {
  if (
    current == null ||
    last == null ||
    last === 0 ||
    isNaN(current) ||
    isNaN(last)
  ) {
    return { text: "--", isPositive: null };
  }

  const pct = ((Number(current) - Number(last)) / Number(last)) * 100;
  if (!Number.isFinite(pct)) return { text: "--", isPositive: null };

  const abs = Math.abs(pct).toFixed(2);
  if (pct > 0) return { text: `${abs}%`, isPositive: true };
  if (pct < 0) return { text: `${abs}%`, isPositive: false };
  return { text: "0.00%", isPositive: null };
}

function getBadgeText(symbolLabel) {
  const cleaned = String(symbolLabel || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
  if (!cleaned) return "--";
  return cleaned.slice(0, 3);
}

function TickerTapeItem({ item, theme }) {
  const { symbolLabel, priceText, change, badgeColor, hasPrice } = item;
  const isDark = theme.palette.mode === "dark";
  const changeColor =
    change.isPositive === null
      ? isDark
        ? "rgba(255, 255, 255, 0.55)"
        : "rgba(0, 0, 0, 0.45)"
      : change.isPositive
        ? isDark
          ? "#4ade80"
          : "#16a34a"
        : isDark
          ? "#f87171"
          : "#dc2626";

  return (
    <Box
      className="ticker-tape-item"
      sx={{
        border: `1px solid ${
          isDark ? "rgba(255, 255, 255, 0.14)" : "rgba(0, 0, 0, 0.12)"
        }`,
        backgroundColor: isDark
          ? "rgba(255, 255, 255, 0.03)"
          : "rgba(0, 0, 0, 0.02)",
      }}
    >
      <Box
        className="ticker-tape-badge"
        sx={{
          backgroundColor: badgeColor,
          color: "#fff",
        }}
      >
        {getBadgeText(symbolLabel)}
      </Box>

      <Box className="ticker-tape-content">
        <Box
          component="span"
          className="ticker-tape-symbol"
          sx={{ color: theme.palette.text.primary }}
        >
          {symbolLabel}
        </Box>

        <Box className="ticker-tape-price-row">
          <Box
            component="span"
            className="ticker-tape-price"
            sx={{ color: theme.palette.text.primary }}
          >
            {hasPrice ? `$ ${priceText}` : priceText}
          </Box>

          <Box
            className="ticker-tape-change"
            sx={{ color: changeColor, marginLeft: "auto" }}
          >
            <Box className="ticker-tape-change-icon-slot">
              {change.isPositive === true && (
                <ArrowDropUpIcon
                  className="ticker-tape-change-icon"
                  sx={{ color: changeColor }}
                />
              )}
              {change.isPositive === false && (
                <ArrowDropDownIcon
                  className="ticker-tape-change-icon"
                  sx={{ color: changeColor }}
                />
              )}
            </Box>
            <Box
              component="span"
              className="ticker-tape-change-text"
              sx={{ color: changeColor }}
            >
              {change.text}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function hasFetchedMarketData(marketData) {
  return Array.isArray(marketData) && marketData.length > 0;
}

const TickerTape = () => {
  const theme = useTheme();
  const marketData = useSelector(selectMarketData);
  const livePrices = useSelector(selectLivePrices);

  const hasValidMarketData = useMemo(
    () => hasFetchedMarketData(marketData),
    [marketData]
  );

  const tapeItems = useMemo(() => {
    const entryMap = hasValidMarketData
      ? buildMergedEntryMap(marketData, livePrices)
      : new Map();
    const usedKeys = new Set();
    const pricedItems = [];

    const pushPriced = (symbol, entry) => {
      const current = entry?.current;
      if (!hasValidPrice(current)) return false;

      const key = normalizeSymbolKey(symbol);
      if (usedKeys.has(key)) return false;

      usedKeys.add(key);
      pricedItems.push({
        key: String(symbol).toUpperCase(),
        symbolLabel: String(symbol).toUpperCase(),
        priceText: formatValue(current) ?? "--",
        change: formatPctFromMarket(current, entry?.last),
        hasPrice: true,
        badgeColor: BADGE_COLORS[pricedItems.length % BADGE_COLORS.length],
      });
      return true;
    };

    // Prefer DEFAULT_TICKERS that have real prices.
    DEFAULT_TICKERS.forEach((symbol) => {
      pushPriced(symbol, entryMap.get(normalizeSymbolKey(symbol)));
    });

    // Replace missing defaults with other priced symbols from market data.
    const targetCount = DEFAULT_TICKERS.length;
    if (pricedItems.length < targetCount) {
      for (const [key, entry] of entryMap) {
        if (pricedItems.length >= targetCount) break;
        pushPriced(entry.symbol || key, entry);
      }
    }

    // Until real prices exist, keep showing the default ticker list.
    if (pricedItems.length > 0) return pricedItems;

    return DEFAULT_TICKERS.map((symbol, index) => ({
      key: symbol,
      symbolLabel: symbol,
      priceText: "--",
      change: { text: "--", isPositive: null },
      hasPrice: false,
      badgeColor: BADGE_COLORS[index % BADGE_COLORS.length],
    }));
  }, [hasValidMarketData, marketData, livePrices]);

  const renderTrack = (suffix) =>
    tapeItems.map((item) => (
      <TickerTapeItem key={`${item.key}-${suffix}`} item={item} theme={theme} />
    ));

  const trackSets = ["a", "b", "c", "d", "e", "f"];

  return (
    <Box
      className="ticker-tape"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box className="ticker-tape-viewport">
        <Box
          className="ticker-tape-track"
          style={{ "--ticker-sets": trackSets.length }}
        >
          {trackSets.map((suffix) => renderTrack(suffix))}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(TickerTape);
